import * as React from 'react';
import { useState } from 'react';
import HollywoodTourLogoIcon from '../../../assets/images/icons/svg/hollywood-tour-logo.svg';
import LondonTourLogoIcon from '../../../assets/images/icons/svg/london-tour-logo.svg';
import { useTranslations } from '../../../common/translation';
import { MARKET } from '../../../config/browser';
import { TourFeatureBanner } from '../tour-feature-banner/TourFeatureBanner';
import { TourFeatureVideo } from '../tour-feature-video/TourFeatureVideo';
import translations from '../translations';
import { FeatureVideo, VideoName } from './StudioTours.props';
import { StyledStudioTours } from './StudioTours.styles';

export const HOLLYWOOD_TOUR_URL = 'https://www.wbstudiotour.com';
export const HOLLYWOOD_TOUR_TICKETS_URL = 'https://www.wbstudiotour.com/tickets';
export const LONDON_TOUR_URL = 'https://www.wbstudiotour.co.uk';
export const LONDON_TOUR_TICKETS_URL =
  MARKET === 'DE' ? 'https://www.wbstudiotour.co.uk/de/tickets' : 'https://www.wbstudiotour.co.uk/tickets';
export const TOKYO_TOUR_URL = 'https://www.wbstudiotour.jp/';

const splitTitle = (title: string): JSX.Element => {
  // Split on nearest space
  const midpoint = Math.floor(title.length / 2);
  let splitIndex;
  for (let i = 0; i < midpoint; i++) {
    if (title[midpoint - i] === ' ') {
      splitIndex = midpoint - i;
      break;
    }

    if (title[midpoint + i] === ' ') {
      splitIndex = midpoint + i;
      break;
    }
  }

  if (!splitIndex) {
    return <div>{title}</div>;
  }

  const firstPart = title.slice(0, splitIndex);
  const secondPart = title.slice(splitIndex + 1);

  return (
    <>
      <div>{firstPart}</div>
      <div>{secondPart}</div>
    </>
  );
};

export const StudioTours = () => {
  const [videos, setVideos] = useState<{ [K in VideoName]?: FeatureVideo }>({});
  const { t } = useTranslations(translations);

  const onReady = (videoName: VideoName) => (event: any) =>
    setVideos({ ...videos, [videoName]: { isOpen: true, player: event.target } });
  const openVideo = (videoName: VideoName) => () => {
    if (!videos[videoName] || !videos[videoName]!.isOpen) {
      // Pause all other videos
      Object.keys(videos).forEach(
        (vn: VideoName) => videos[vn] && videos[vn]!.player && videos[vn]!.player.pauseVideo(),
      );
      setVideos({ ...videos, [videoName]: { isOpen: true } });
    }
  };
  const closeVideo = (videoName: VideoName) => () => setVideos({ ...videos, [videoName]: { isOpen: false } });

  return (
    <StyledStudioTours>
      {videos.hollywood && videos.hollywood.isOpen && (
        <TourFeatureVideo
          aria-label='Studio Tour Hollywood'
          first={true}
          onClose={closeVideo('hollywood')}
          onReady={onReady('hollywood')}
          ticketsHref={HOLLYWOOD_TOUR_TICKETS_URL}
          title={
            <>
              Studio Tour
              <span>Hollywood</span>
            </>
          }
          videoId='k-8eRwKK0Hs'
          websiteHref={HOLLYWOOD_TOUR_URL}
        />
      )}
      {videos.london && videos.london.isOpen && (
        <TourFeatureVideo
          aria-label='Studio Tour London'
          onClose={closeVideo('london')}
          onReady={onReady('london')}
          ticketsHref={LONDON_TOUR_TICKETS_URL}
          title={
            <>
              Studio Tour
              <span>London</span>
            </>
          }
          videoId='zOCnDMlH8eU'
          websiteHref={LONDON_TOUR_URL}
        />
      )}
      <TourFeatureBanner
        imageSrc='/assets/experiences/tours/hollywood-banner.jpg'
        logo={HollywoodTourLogoIcon}
        onPreviewClick={openVideo('hollywood')}
        superTitle='Warner Bros. Studio Tour Hollywood'
        ticketsHref={HOLLYWOOD_TOUR_TICKETS_URL}
        title={splitTitle(t('madeHereTitle', 'Hollywood Made Here'))}
        websiteHref={HOLLYWOOD_TOUR_URL}
      />
      <TourFeatureBanner
        imageSrc='/assets/experiences/tours/london-banner.jpg'
        logo={LondonTourLogoIcon}
        onPreviewClick={openVideo('london')}
        superTitle='Warner Bros. Studio Tour London'
        ticketsHref={LONDON_TOUR_TICKETS_URL}
        title={splitTitle(t('makingOfTitle', 'The Making of Harry Potter'))}
        websiteHref={LONDON_TOUR_URL}
      />
      <TourFeatureBanner
        imageSrc='/assets/experiences/tours/japan-banner.jpg'
        logo={{ alt: 'Studio Tour Tokyo', src: '/assets/experiences/tours/japan-logo.png' }}
        superTitle='Warner Bros. Studio Tour Tokyo'
        title={splitTitle(t('makingOfTitle', 'The Making of Harry Potter'))}
        websiteHref={TOKYO_TOUR_URL}
      />
    </StyledStudioTours>
  );
};
